import React, { useCallback, useRef } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Coins,
  GripVertical,
  Tag,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FloorplanState,
  FloorplanConfig,
  FloorplanRoomLabel,
} from '@/types';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';
import { formatCredits } from '@/lib/utils';

// ============================================================================
// Constants
// ============================================================================

const ROOM_LABEL_PILLS = [
  { type: 'living_room', label: 'Wohnzimmer' },
  { type: 'kitchen', label: 'Küche' },
  { type: 'bathroom', label: 'Bad' },
  { type: 'bedroom', label: 'Schlafzimmer' },
  { type: 'hallway', label: 'Flur' },
  { type: 'dining', label: 'Esszimmer' },
  { type: 'office', label: 'Büro' },
  { type: 'walk_in_closet', label: 'Ankleide' },
  { type: 'staircase', label: 'Treppenhaus' },
  { type: 'balcony', label: 'Balkon' },
  { type: 'terrace', label: 'Terrasse' },
  { type: 'custom', label: 'Sonstiges' },
];

// ============================================================================
// Props
// ============================================================================

interface FloorplanLabelsViewProps {
  floorplanState: FloorplanState;
  onFloorplanStateUpdate: (updates: Partial<FloorplanState>) => void;
  onNext: () => void;
  onBack: () => void;
  credits: number;
}

// ============================================================================
// Component
// ============================================================================

export const FloorplanLabelsView: React.FC<FloorplanLabelsViewProps> = ({
  floorplanState,
  onFloorplanStateUpdate,
  onNext,
  onBack,
  credits,
}) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const config = floorplanState.config;
  const displayImageUrl = floorplanState.extractedUnitUrl || floorplanState.previewUrl;

  // ========================================================================
  // Config Update Helper
  // ========================================================================

  const updateConfig = useCallback(
    (updates: Partial<FloorplanConfig>) => {
      onFloorplanStateUpdate({
        config: { ...config, ...updates },
      });
    },
    [config, onFloorplanStateUpdate]
  );

  // ========================================================================
  // Label Drag & Drop
  // ========================================================================

  const handlePillDragStart = useCallback(
    (e: React.DragEvent, type: string, label: string) => {
      e.dataTransfer.setData('text/plain', JSON.stringify({ type, label }));
      e.dataTransfer.effectAllowed = 'copy';
    },
    []
  );

  const handleImageDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const container = imageContainerRef.current;
      if (!container) return;

      const data = e.dataTransfer.getData('text/plain');
      if (!data) return;

      try {
        const { type, label } = JSON.parse(data);
        const rect = container.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const newLabel: FloorplanRoomLabel = {
          id: `label-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          type,
          label,
          x: Math.max(0.02, Math.min(0.98, x)),
          y: Math.max(0.02, Math.min(0.98, y)),
        };

        updateConfig({
          roomLabels: [...config.roomLabels, newLabel],
        });
      } catch {
        // Invalid data
      }
    },
    [config.roomLabels, updateConfig]
  );

  const handleImageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleLabelMove = useCallback(
    (labelId: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const container = imageContainerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      const label = config.roomLabels.find((l) => l.id === labelId);
      if (!label) return;

      const startLabelX = label.x;
      const startLabelY = label.y;

      const onMove = (ev: MouseEvent) => {
        const dx = (ev.clientX - startX) / containerRect.width;
        const dy = (ev.clientY - startY) / containerRect.height;
        const newX = Math.max(0.02, Math.min(0.98, startLabelX + dx));
        const newY = Math.max(0.02, Math.min(0.98, startLabelY + dy));

        updateConfig({
          roomLabels: config.roomLabels.map((l) =>
            l.id === labelId ? { ...l, x: newX, y: newY } : l
          ),
        });
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [config.roomLabels, updateConfig]
  );

  const handleRemoveLabel = useCallback(
    (labelId: string) => {
      updateConfig({
        roomLabels: config.roomLabels.filter((l) => l.id !== labelId),
      });
    },
    [config.roomLabels, updateConfig]
  );

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Raumbezeichnungen</h1>
            <p className="text-sm text-muted-foreground">
              Räume identifizieren für die KI-Generierung
            </p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          {formatCredits(credits)} Credits
        </Badge>
      </div>

      <FloorplanStepIndicator currentStep="labels" />

      {/* Main Content: Two Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel: Image + Labels */}
        <div className="lg:col-span-3 space-y-4">
          <Card>
            <CardContent className="p-4">
              <div
                ref={imageContainerRef}
                className="relative rounded-lg overflow-hidden bg-muted/30 border select-none"
                onDrop={handleImageDrop}
                onDragOver={handleImageDragOver}
              >
                {displayImageUrl && (
                  <img
                    src={displayImageUrl}
                    alt="Grundriss"
                    className="w-full h-auto"
                    draggable={false}
                  />
                )}

                {/* Rendered Room Labels */}
                {config.roomLabels.map((label) => (
                  <div
                    key={label.id}
                    className="absolute flex items-center gap-1 cursor-move group"
                    style={{
                      left: `${label.x * 100}%`,
                      top: `${label.y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20,
                    }}
                    onMouseDown={(e) => handleLabelMove(label.id, e)}
                  >
                    <div className="flex items-center gap-1.5 rounded-full bg-white/95 border border-border shadow-md px-3 py-1.5 text-xs font-semibold text-foreground backdrop-blur-sm transition-shadow hover:shadow-lg">
                      <GripVertical className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      {label.label}
                      <button
                        className="w-4 h-4 rounded-full bg-destructive/10 text-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/20"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLabel(label.id);
                        }}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Drop hint overlay */}
                {!config.roomsLabeled && config.roomLabels.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/40 backdrop-blur-sm rounded-xl px-6 py-3 text-white text-sm font-medium">
                      Raumbezeichnungen auf den Grundriss ziehen
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Label Palette */}
          {!config.roomsLabeled && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Raumbezeichnungen — auf den Grundriss ziehen
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ROOM_LABEL_PILLS.map((pill) => (
                    <div
                      key={pill.type}
                      draggable
                      onDragStart={(e) =>
                        handlePillDragStart(e, pill.type, pill.label)
                      }
                      className="
                        inline-flex items-center gap-1.5 rounded-full border border-border
                        bg-background px-3 py-1.5 text-xs font-medium
                        cursor-grab active:cursor-grabbing
                        hover:bg-primary/5 hover:border-primary/30
                        transition-colors select-none
                      "
                    >
                      <GripVertical className="w-3 h-3 text-muted-foreground" />
                      {pill.label}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel: Controls */}
        <div className="lg:col-span-2 space-y-4 lg:sticky lg:top-24 lg:self-start">
          {/* Room Labels Question */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold tracking-tight">
                Sind Räume bereits beschriftet?
              </h3>
              <p className="text-xs text-muted-foreground">
                Falls der Grundriss bereits Raumbezeichnungen enthält, erkennt die KI diese automatisch.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={config.roomsLabeled ? 'default' : 'outline'}
                  onClick={() => updateConfig({ roomsLabeled: true })}
                >
                  Ja — bereits beschriftet
                </Button>
                <Button
                  size="sm"
                  variant={!config.roomsLabeled ? 'default' : 'outline'}
                  onClick={() => updateConfig({ roomsLabeled: false })}
                >
                  Nein — manuell zuweisen
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          {!config.roomsLabeled && config.roomLabels.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <Badge variant="secondary" className="gap-1">
                  <Tag className="w-3 h-3" />
                  {config.roomLabels.length} Bezeichnung{config.roomLabels.length !== 1 ? 'en' : ''} platziert
                </Badge>
              </CardContent>
            </Card>
          )}

          {config.roomsLabeled && (
            <Card className="bg-muted/30 border-dashed">
              <CardContent className="p-4 text-sm text-muted-foreground">
                Die KI erkennt die vorhandenen Beschriftungen im Grundriss automatisch.
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <Button onClick={onNext} className="w-full gap-2">
            Weiter zu Einbauten
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={onBack} className="w-full gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Button>
        </div>
      </div>
    </div>
  );
};
